import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import { MdClose, MdCheckCircle } from "react-icons/md";

const EmailVerificationModal = ({
  show,
  onClose,
  onVerified,
  newEmail,
  isEmailChange = false, // New prop to differentiate from login OTP
}) => {
  const [animate, setAnimate] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [step, setStep] = useState("otp"); // 'otp' or 'success'
  const otpSentRef = useRef(false);

  useLockBodyScroll(show);

  useEffect(() => {
    if (!show) {
      setOtp("");
      setTimer(0);
      setStep("otp");
      otpSentRef.current = false;
      return;
    }
    setTimeout(() => {
      setAnimate(true);
    }, 10);
  }, [show]);

  // Send OTP when modal opens
  useEffect(() => {
    if (show && !otpSentRef.current && newEmail) {
      otpSentRef.current = true;
      sendEmailOTP();
    }
  }, [show, newEmail]);

  // Timer effect
  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const sendEmailOTP = async () => {
    try {
      setSending(true);

      // Add a 30-second timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const res = await axios.post(
          "/otp/send-otp",
          {
            identifier: newEmail,
          },
          { signal: controller.signal }
        );

        clearTimeout(timeoutId);

        if (res.data.success) {
          toast.success("Verification code sent!");
          setTimer(30); // 30 seconds countdown
        }
      } catch (err) {
        clearTimeout(timeoutId);

        if (err.code === 'ECONNABORTED') {
          toast.error("Request timeout. Please check your connection and try again.");
        } else {
          toast.error(err?.response?.data?.message || "Failed to send OTP");
        }

        // Don't close modal on timeout - let user retry
        if (err.code !== 'ECONNABORTED') {
          handleClose();
        }
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
      handleClose();
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      return toast.error("Enter 6-digit code");
    }

    try {
      setLoading(true);

      // Add a 30-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const res = await axios.post(
          "/otp/verify-otp",
          {
            identifier: newEmail,
            otp,
          },
          { signal: controller.signal }
        );

        clearTimeout(timeoutId);

        if (res.data.success) {
          setStep("success");

          // For email change, update the email and mark as verified
          if (isEmailChange) {
            setTimeout(() => {
              onVerified(newEmail, true);
              handleClose();
            }, 2000);
          } else {
            setTimeout(() => {
              onVerified(newEmail, res.data.data.user?.isEmailVerified);
              handleClose();
            }, 2000);
          }
        }
      } catch (err) {
        clearTimeout(timeoutId);

        if (err.code === 'ECONNABORTED') {
          toast.error("Request timeout. Please try again.");
        } else {
          toast.error(err?.response?.data?.message || "Invalid OTP");
        }

        setOtp("");
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  if (!show) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-250 ${animate ? "opacity-100" : "opacity-0"
        }`}
    >
      <div
        className={`bg-white dark:bg-gray-900 w-full md:w-[420px] rounded-t-3xl md:rounded-3xl p-6 transition-all duration-250 ${animate
          ? "scale-100 translate-y-0"
          : "scale-95 md:translate-y-4 translate-y-full"
          }`}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition"
        >
          <MdClose size={20} className="text-gray-600 dark:text-gray-400" />
        </button>

        {/* OTP Step */}
        {step === "otp" && (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Verify Your Email
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter the 6-digit code we sent to
                <br />
                <span className="font-semibold text-gray-900 dark:text-white">
                  {newEmail}
                </span>
              </p>
            </div>

            {/* OTP Input */}
            <div className="mb-6">
              <input
                type="text"
                value={otp}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  if (value.length > 6) value = value.slice(0, 6);
                  setOtp(value);
                }}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                className="w-full border-2 border-gray-300 dark:border-gray-600 rounded-xl px-4 py-4 text-center text-3xl tracking-[0.5em] font-semibold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-gray-600 outline-none focus:border-primaryColor dark:focus:border-primaryColor transition"
              />
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerify}
              disabled={loading || otp.length !== 6}
              className="w-full bg-gradient-to-r from-primaryColor to-subPrimaryColor text-white py-3.5 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                  Verifying...
                </span>
              ) : (
                "Verify Code"
              )}
            </button>

            {/* Resend Button */}
            <button
              onClick={sendEmailOTP}
              disabled={sending || timer > 0}
              className="w-full mt-3 text-sm font-semibold text-primaryColor dark:text-blue-400 disabled:text-gray-400 disabled:cursor-not-allowed transition"
            >
              {sending ? (
                "Sending Code..."
              ) : timer > 0 ? (
                <>
                  Resend in{" "}
                  <span className="font-bold">
                    {Math.floor(timer / 60)}:{(timer % 60)
                      .toString()
                      .padStart(2, "0")}
                  </span>
                </>
              ) : (
                "Resend Code"
              )}
            </button>

            {/* Cancel Button */}
            <button
              onClick={handleClose}
              className="w-full mt-3 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            >
              Cancel
            </button>
          </>
        )}

        {/* Success Step */}
        {step === "success" && (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <MdCheckCircle size={64} className="text-green-500 animate-bounce" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Email Verified!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your email has been successfully verified
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EmailVerificationModal;